import React from 'react';

import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import { TextInput } from '@components/Forms/TextInput';
import { SpinnerModal } from '@components/SpinnerModal';
import { useForgetPassword } from '@hooks/mutation/useForgetPassword';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { FormError } from '@utils/errors';
import { formStrings, createValidate, requiredStringField } from '@utils/forms';
import { createStrings } from '@utils/local';
import { PageMeta } from '@utils/pages';
import { Form, Formik } from 'formik';
import * as t from 'io-ts';
import { useHistory } from 'react-router-dom';

const strings = createStrings({
  ja: {
    title: 'パスワードを忘れた場合',
    email: '登録したメールアドレス',
    sendMsg: 'パスワード再設定用メール送信',
    info: '上記メールアドレスに再設定用のURLを送信します。',
    back: 'ログインに戻る',
    resetNotification: 'パスワード再設定用のメールを送りました。',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexCenter,
    backgroundColor: theme.palette.text.primary,
    flex: 1,
    height: '100vh',
  },
  middleContainer: {
    width: 500,
    background: theme.palette.secondary.contrastText,
    padding: '1rem',
  },
  formContainer: {
    ...common.flexColumn,
    ...common.verticalSpace2,
    padding: '40px 69px',
    marginTop: 30,
  },
  title: {
    ...common.normalText,
    ...common.flexCenter,
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.palette.text.primary,
    marginBottom: 24,
  },
  personIcon: {
    paddingRight: 10,
    width: '1.6rem',
    height: '1.6rem',
    color: theme.palette.grey[500],
  },
  tipsContainer: {
    ...common.flexCenter,
    ...common.flexColumn,
    marginTop: 32,
  },
  forgetPassword: {
    color: theme.palette.secondary.contrastText,
  },
  tipsInfo: {
    ...common.middleText,
    color: theme.palette.grey[500],
    padding: '0 24px',
  },
  sendButton: {
    borderRadius: 27,
  },
  tipsBack: {
    ...common.normalText,
    color: theme.palette.primary.main,
    marginTop: 20,
    cursor: 'pointer',
  },
}));
const schema = t.type({
  email: requiredStringField(formStrings.email),
});

const initialValues: t.TypeOf<typeof schema> = {
  email: '',
};

export const ForgetPassword: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const history = useHistory();
  const { mutateAsync, isError, error, isSuccess, isLoading, reset } = useForgetPassword();

  return (
    <div className={classes.container}>
      <div className={classes.middleContainer}>
        <Formik
          validate={createValidate(schema)}
          initialValues={initialValues}
          onSubmit={async (values) => {
            await mutateAsync(values);
            // history.push('/admin/auth/ResetPassword');
          }}
        >
          <>
            <SpinnerModal open={isLoading} />
            <Alert open={isError} severity="error" onClose={reset}>
              {error instanceof FormError && error.getDisplayMessage()}
            </Alert>
            <Alert open={isSuccess} severity="success" onClose={reset}>
              {strings.resetNotification}
            </Alert>
            <Form className={classes.formContainer}>
              <div className={classes.title}>{strings.title}</div>
              <TextInput
                name="email"
                type="email"
                autoComplete="current-email"
                placeholder={strings.email}
                noLabel
                iconBefore={<PersonOutlineIcon className={classes.personIcon} />}
              />

              <Button
                text={strings.sendMsg}
                color="primary"
                className={classes.sendButton}
                type="submit"
              />

              <div className={classes.tipsContainer}>
                <a className={classes.tipsInfo}>{strings.info}</a>
                <a className={classes.tipsBack} onClick={() => history.goBack()}>
                  {strings.back}
                </a>
              </div>
            </Form>
          </>
        </Formik>
      </div>
    </div>
  );
};

export const meta: PageMeta = {};
